import { AfterViewInit, Component, OnInit } from '@angular/core';
import { FormControl, UntypedFormControl } from '@angular/forms';
import {
  StringCustomFieldConfig,
  SharedModule,
  FormInputComponent,
  NotificationService,
  DataService,
  Asset,
} from '@vendure/admin-ui/core';
import { ContactFormData } from '../ui-types';
import { safeJSONParse } from '../utils';

@Component({
  template: `
    <vdr-card>
      <vdr-form-field [label]="'Contact Form Type'" class="mb-2">
        <select [formControl]="contactFormTypeControl">
          <option value="email-form">Email</option>
        </select>
      </vdr-form-field>

      <vdr-form-field [label]="'Send To Email Address'" class="mb-2">
        <input type="text" [formControl]="emailControl" />
      </vdr-form-field>

      <vdr-form-field [label]="'Featured Asset'">
        <vdr-dynamic-form-input
          [control]="featuredAssetControl"
          [def]="{
            type: 'relation',
            entity: 'Asset',
            ui: {
              component: 'relation-form-input',
            }
          }"
        >
        </vdr-dynamic-form-input>
      </vdr-form-field>
    </vdr-card>
  `,
  standalone: true,
  imports: [SharedModule],
})
export class ContactFormComponent
  implements FormInputComponent<StringCustomFieldConfig>, OnInit, AfterViewInit
{
  readonly: boolean;
  config: StringCustomFieldConfig;
  formControl: FormControl;

  contactFormData: ContactFormData;

  contactFormTypeControl = new FormControl();
  emailControl = new FormControl();
  featuredAssetControl = new UntypedFormControl();

  constructor(
    private notificationService: NotificationService,
    private dataService: DataService
  ) {}

  ngOnInit(): void {
    const defaultContactFormData: ContactFormData = {
      contactFormType: '',
      email: '',
      featuredAssetId: null,
    };
    this.contactFormData = safeJSONParse<ContactFormData>(
      this.formControl.value,
      this.notificationService,
      defaultContactFormData,
    )!;
    this.initializeFormControls();

    if (this.contactFormData?.featuredAssetId) {
      this.dataService.product
        .getAsset(this.contactFormData.featuredAssetId.toString())
        .mapSingle((data) => data.asset)
        .subscribe((asset) => {
          this.featuredAssetControl.setValue(asset);
        });
    }
  }

  ngAfterViewInit() {
    this.contactFormTypeControl.valueChanges.subscribe(this.onContactFormTypeChange);
    this.emailControl.valueChanges.subscribe(this.onEmailChange);
    this.featuredAssetControl.valueChanges.subscribe(this.onFeaturedAssetChange);
  }

  private initializeFormControls() {
    this.emailControl.setValue(this.contactFormData?.email);
    this.contactFormTypeControl.setValue(this.contactFormData?.contactFormType);
    this.featuredAssetControl.setValue(null);
  }

  private onContactFormTypeChange = (value: string) => {
    if (this.contactFormData?.contactFormType !== value) {
      this.contactFormData!.contactFormType = value;
      this.updateJsonString();
    }
  };

  private onEmailChange = (value: string) => {
    if (this.contactFormData?.email !== value) {
      this.contactFormData!.email = value;
      this.updateJsonString();
    }
  };

  private onFeaturedAssetChange = (value: Asset) => {
    const featuredAssetId = value ? value.id : null;
    if (this.contactFormData?.featuredAssetId !== featuredAssetId) {
      this.contactFormData!.featuredAssetId = featuredAssetId;
      this.updateJsonString();
    }
  };

  private updateJsonString() {
    const jsonString = JSON.stringify(this.contactFormData);
    this.formControl.setValue(jsonString);
    this.formControl.markAsDirty();
  }
}
